import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEur, fmtMwhc } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CompareTable } from "@/components/comparator/CompareTable";
import { OpsTable } from "@/components/comparator/OpsTable";
import { InputsTable } from "@/components/comparator/InputsTable";
import { PrimeChart } from "@/components/comparator/PrimeChart";
import { printComparison } from "@/components/comparator/printComparison";
import { Copy, Pencil, Download, FileText, FolderPlus, GitCompareArrows, GripVertical, Loader2, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SimChip = ({ s, checked, isRef, onToggle, onRef, onDelete, onSave, onOpen, drag }) => (
  <Card {...drag.dnd} className={`p-4 transition-[border-color,opacity,box-shadow] ${checked ? "border-genis/60" : ""} ${drag.dragging ? "opacity-40" : ""} ${drag.over ? "ring-2 ring-genis" : ""}`} data-testid={`sim-card-${s.id}`}>
    <div className="flex items-start gap-3">
      <GripVertical className="h-4 w-4 mt-1 -ml-1 shrink-0 text-muted-foreground cursor-grab active:cursor-grabbing" title="Glisser pour réorganiser" data-testid={`sim-drag-${s.id}`} />
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-1" data-testid={`sim-check-${s.id}`} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold truncate">{s.name}</div>
        <div className="text-xs text-muted-foreground truncate">{s.client || "—"} · zone {s.zone} · {(s.result?.operations || []).length} opération(s)</div>
        <div className="mt-2 flex items-baseline gap-3">
          <span className="metric font-bold text-genis">{fmtEur(s.result?.total_prime)}</span>
          <span className="metric text-xs text-muted-foreground">{fmtMwhc(s.result?.total_kwhc)}</span>
        </div>
      </div>
    </div>
    <div className="mt-3 flex items-center gap-1.5">
      <Button size="sm" variant={isRef ? "default" : "outline"} onClick={onRef} disabled={!checked} className={`h-8 text-xs ${isRef ? "bg-genis hover:bg-genis-dark text-white" : ""}`} data-testid={`sim-ref-${s.id}`}>
        <Star className="h-3.5 w-3.5 mr-1" /> {isRef ? "Référence" : "Définir référence"}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onOpen("dup")} className="h-8 text-xs" title="Dupliquer et modifier (variante)" data-testid={`sim-dup-${s.id}`}><Copy className="h-3.5 w-3.5" /></Button>
      <Button size="sm" variant="ghost" onClick={() => onOpen("edit")} className="h-8 text-xs" title="Modifier cette simulation" data-testid={`sim-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button size="sm" variant="ghost" onClick={onSave} className="h-8 text-xs" title="Enregistrer comme projet" data-testid={`sim-save-${s.id}`}><FolderPlus className="h-3.5 w-3.5" /></Button>
      <Button size="sm" variant="ghost" onClick={onDelete} className="h-8 text-xs text-destructive hover:text-destructive ml-auto" title="Retirer" data-testid={`sim-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </div>
  </Card>
);

const exportXlsx = async (ids, ref) => {
  try {
    const r = await api.get("/comparator/export", { params: { ids: ids.join(","), ref }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    Object.assign(document.createElement("a"), { href: url, download: `comparaison-${new Date().toISOString().slice(0, 10)}.xlsx` }).click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Échec de l'export Excel");
  }
};

export default function Comparator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sims, setSims] = useState(null);
  const [fichesByCode, setFichesByCode] = useState({});
  useEffect(() => { api.get("/fiches").then((r) => setFichesByCode(Object.fromEntries(r.data.map((f) => [f.code, f])))); }, []);
  const [selected, setSelected] = useState([]);
  const [refId, setRefIdState] = useState(() => localStorage.getItem("cee_comparator_ref"));
  const setRefId = (id) => { localStorage.setItem("cee_comparator_ref", id); setRefIdState(id); };

  const load = () => api.get("/comparator").then((r) => {
    setSims(r.data);
    setSelected((sel) => (sel.length ? sel.filter((id) => r.data.some((s) => s.id === id)) : r.data.slice(0, 6).map((s) => s.id)));
  });
  useEffect(() => { load(); }, []);

  const chosen = useMemo(() => (sims || []).filter((s) => selected.includes(s.id)), [sims, selected]);
  const refSim = chosen.find((s) => s.id === refId) || chosen[0];

  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : sel.length >= 6 ? (toast.error("6 simulations maximum"), sel) : [...sel, id]));
  const remove = async (id) => { await api.delete(`/comparator/${id}`); setSelected((sel) => sel.filter((x) => x !== id)); load(); };
  const clearAll = async () => { await api.delete("/comparator"); setSelected([]); load(); };
  const saveAsProject = async (id) => {
    const { data } = await api.post(`/comparator/${id}/project`);
    toast.success("Projet enregistré", { action: { label: "Ouvrir", onClick: () => navigate(`/projects/${data.id}`) } });
  };

  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = sims.map((s) => s.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId) + (sims.findIndex((s) => s.id === dragId) < sims.findIndex((s) => s.id === targetId) ? 1 : 0), 0, dragId);
    setSims(ids.map((id) => sims.find((s) => s.id === id)));
    api.put("/comparator/order", { ids }).catch(() => toast.error("Impossible d'enregistrer l'ordre"));
  };
  const dragProps = (id) => ({
    dragging: dragId === id,
    over: overId === id && dragId !== id,
    dnd: {
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.effectAllowed = "move"; setDragId(id); },
    onDragOver: (e) => { e.preventDefault(); if (overId !== id) setOverId(id); },
    onDragLeave: () => setOverId((o) => (o === id ? null : o)),
    onDrop: (e) => { e.preventDefault(); dropOn(id); setDragId(null); setOverId(null); },
    onDragEnd: () => { setDragId(null); setOverId(null); },
    },
  });

  if (!sims) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-genis" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="comparator-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow text-genis">Aide à la décision</div>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1">Comparateur de simulations</h1>
          <p className="text-muted-foreground mt-1">Enchaînez les simulations depuis « Nouvelle simulation » puis comparez-les côte à côte (2 à 6). Glissez-déposez les cartes pour choisir l'ordre des colonnes.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate("/simulation/new")} className="bg-genis hover:bg-genis-dark text-white" data-testid="comparator-new-sim-btn"><Plus className="h-4 w-4 mr-1" /> Nouvelle simulation</Button>
          <Button variant="outline" disabled={chosen.length < 2} onClick={() => printComparison(chosen, refSim, user?.partner?.primary_color, fichesByCode)} data-testid="comparator-pdf-btn"><FileText className="h-4 w-4 mr-1" /> Exporter en PDF</Button>
          <Button variant="outline" disabled={chosen.length < 2} onClick={() => exportXlsx(chosen.map((s) => s.id), refSim.id)} data-testid="comparator-xlsx-btn"><Download className="h-4 w-4 mr-1" /> Exporter Excel</Button>
          {sims.length > 0 && <Button variant="ghost" onClick={clearAll} className="text-destructive hover:text-destructive" data-testid="comparator-clear-btn"><Trash2 className="h-4 w-4 mr-1" /> Tout vider</Button>}
        </div>
      </div>

      {sims.length === 0 ? (
        <Card className="p-12 text-center" data-testid="comparator-empty">
          <GitCompareArrows className="h-10 w-10 text-genis mx-auto" />
          <div className="font-heading font-bold text-lg mt-3">Aucune simulation dans le comparateur</div>
          <p className="text-sm text-muted-foreground mt-1">Dans la synthèse d'une simulation, cliquez sur « Ajouter au comparateur et enchaîner ».</p>
        </Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sims.map((s) => (
              <SimChip key={s.id} s={s} drag={dragProps(s.id)} checked={selected.includes(s.id)} isRef={refSim?.id === s.id} onToggle={() => toggle(s.id)} onRef={() => setRefId(s.id)} onDelete={() => remove(s.id)} onSave={() => saveAsProject(s.id)} onOpen={(mode) => navigate(`/simulation/new?cmp=${s.id}&mode=${mode}`)} />
            ))}
          </div>
          {chosen.length < 2 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="comparator-need-two">Sélectionnez au moins 2 simulations pour les comparer.</Card>
          ) : (
            <div className="space-y-6">
              <Card className="p-5"><h2 className="font-heading font-bold text-lg mb-3">Primes par simulation</h2><PrimeChart sims={chosen} /></Card>
              <div><h2 className="font-heading font-bold text-lg mb-3">Synthèse côte à côte</h2><CompareTable sims={chosen} refSim={refSim} /></div>
              <div><h2 className="font-heading font-bold text-lg mb-3">Opérations par fiche</h2><OpsTable sims={chosen} refSim={refSim} /></div>
              <div>
                <h2 className="font-heading font-bold text-lg">Paramètres d'entrée</h2>
                <p className="text-xs text-muted-foreground mb-3">Champs saisis pour chaque fiche. <span className="rounded bg-orange-100 px-1.5 py-0.5 font-semibold text-orange-800">Surligné</span> = valeur différente de la simulation de référence.</p>
                <InputsTable sims={chosen} refSim={refSim} fichesByCode={fichesByCode} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
